/**
 * Species labels the backend treats as categories rather than taxa. These are the
 * only values rendered upright - anything else is a taxon name and gets italics.
 */
const CATEGORY_LABELS = ["mosquito", "bycatch", "debris"];

/**
 * Categories worth reporting as having been overridden. "Mosquito" is what the
 * detector gives everything it does not reject, so a species contributed on top
 * of it replaces no real verdict and there is nothing to reveal on hover.
 */
const OVERRIDABLE_CATEGORIES = ["bycatch", "debris"];

/**
 * Canonical display form: first letter capitalized, everything after it lowercase,
 * with whitespace trimmed and collapsed - "Aedes albopictus".
 *
 * The backend already normalizes contributed_species on write
 * (normalizeSpeciesName in scout-web-backend), but algorithm_taxonomy and
 * category_str come from other producers, so the rule is re-applied here.
 */
export const normalizeSpeciesName = (species: string): string => {
  const trimmed = species.trim().replace(/\s+/g, " ");
  if (!trimmed) return "";
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
};

/** A label plus how it should be rendered. */
interface SpeciesLabel {
  label: string;
  /** Taxon names are italicized; the mosquito/bycatch/debris categories are not. */
  isItalic: boolean;
}

/** Who contributed the label, from the viewer's point of view. */
export type Contributor = "you" | "colleague";

export interface SpeciesDisplay extends SpeciesLabel {
  /**
   * Set only when the displayed label is a human contribution rather than Scout's
   * own, and then to whether the viewer is the one who made it.
   */
  contributor?: Contributor;
  /**
   * What the specimen read as before a human contributed a species, set only when
   * that differs from what is displayed - the header shows it as "Scout said ...".
   */
  overridden?: SpeciesLabel;
}

const sameEmail = (
  a: string | null | undefined,
  b: string | null | undefined,
) => Boolean(a && b && a.trim().toLowerCase() === b.trim().toLowerCase());

const describe = (label: string): SpeciesLabel => ({
  label,
  isItalic: !CATEGORY_LABELS.includes(label.toLowerCase()),
});

/**
 * The category a label implies, or null when the label is not a verdict - a blank
 * label, or the detection library's `class_<n>` placeholder. Any other real label
 * is a species, and every species is a mosquito. Mirrors categoryForLabel in the
 * backend's src/utils/categories.ts.
 */
const categoryForLabel = (label: string | null | undefined): string | null => {
  const trimmed = (label ?? "").trim().toLowerCase();
  if (!trimmed) return null;
  if (/^class_\d+$/.test(trimmed.replace(/\s+/g, "_"))) return null;
  return OVERRIDABLE_CATEGORIES.includes(trimmed) ? trimmed : "mosquito";
};

/**
 * The category the specimen carried before anyone contributed a species, when that
 * category was a real verdict rather than the mosquito default.
 *
 * category_str cannot answer this on its own: it is the *effective* category, and a
 * contribution rewrites it - a species on a bycatch row flips it to "mosquito"
 * (effectiveCategoryExpr in the backend's updateDetection). So the precedence below
 * is the backend's effectiveCategory with contributed_species dropped: the
 * classifier's verdict, else the detector's, else the stored category for rows
 * predating the migration that split original_category_str out.
 */
const overriddenCategory = (detection: {
  category_str?: string | null;
  original_category_str?: string | null;
  classification_classname?: string | null;
}): string => {
  const category =
    categoryForLabel(detection.classification_classname) ??
    (detection.original_category_str ?? detection.category_str ?? "")
      .trim()
      .toLowerCase();
  return OVERRIDABLE_CATEGORIES.includes(category)
    ? normalizeSpeciesName(category)
    : "";
};

/**
 * A detection's species is its contributed_species if it has one, otherwise its
 * algorithm_taxonomy - the same precedence as effectiveSpecies in the backend.
 * With neither, the detector's category is all there is to show.
 */
export const getSpeciesDisplay = (
  detection: {
    contributed_species?: string | null;
    algorithm_taxonomy?: string | null;
    category_str?: string | null;
    original_category_str?: string | null;
    classification_classname?: string | null;
    updated_by?: string | null;
  },
  viewerEmail?: string | null,
): SpeciesDisplay | undefined => {
  const contributed = normalizeSpeciesName(detection.contributed_species ?? "");
  const algorithm = normalizeSpeciesName(detection.algorithm_taxonomy ?? "");
  const label =
    contributed ||
    algorithm ||
    normalizeSpeciesName(detection.category_str ?? "");

  if (!label) return undefined;

  // The classifier's species if it produced one, otherwise the category it or the
  // detector settled on. Normalized on both sides, so a recapitalization of the
  // classifier's own label does not read as an override.
  const prior = algorithm || overriddenCategory(detection);

  return {
    ...describe(label),
    // updated_by is the last person to write the row, which is the contributor
    // for every path that sets a species. It is also stamped by a bookmark or a
    // notes edit, so a teammate touching the specimen afterwards reattributes the
    // label to them - the backend has no dedicated contributed_by to key off.
    contributor: contributed
      ? sameEmail(detection.updated_by, viewerEmail)
        ? "you"
        : "colleague"
      : undefined,
    overridden: prior && prior !== label ? describe(prior) : undefined,
  };
};
